"use client";

import { useMemo, useState } from "react";
import { X, ArrowRight, Plus, ArrowUp } from "lucide-react";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "./ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from "./ui/dropdown-menu";
import {
  TRANSACTION_HISTORY_FILTER_STATUS_OPTIONS,
  TRANSACTION_HISTORY_FILTER_TYPE_OPTIONS,
} from "../constants/transactionHistoryFilters";
import filterIcon from "../assets/images/trading/filter.svg";
import sortIcon from "../assets/images/trading/sort.svg";
import { useBusinessWalletTransactions } from "../hooks/query/businessWalletQueries";
import {
  mapBusinessApiTxToRow,
  getBusinessWalletTransactionSortAmount,
} from "../utils/businessSuiteTransactionRowMap";

const StatusIcon = ({ color }) => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 10 10"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="w-[10px] h-[10px]"
  >
    <g clipPath="url(#clip0_bw_all)">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M5.73034 0.302516C5.32699 -0.100838 4.67302 -0.100839 4.26966 0.302515L3.41081 1.16138H2.19412C1.62369 1.16138 1.16127 1.6238 1.16127 2.19423V3.41091L0.302516 4.26966C-0.100838 4.67301 -0.100839 5.32698 0.302515 5.73034L1.16127 6.58909V7.80464C1.16127 8.37507 1.62369 8.8375 2.19412 8.8375H3.40965L4.26966 9.6975C4.67301 10.1009 5.32698 10.1009 5.73034 9.6975L6.59035 8.8375H7.8045C8.37493 8.8375 8.83736 8.37507 8.83736 7.80464V6.59045L9.6975 5.73034C10.1009 5.32699 10.1009 4.67302 9.6975 4.26966L8.83736 3.40955V2.19423C8.83736 1.6238 8.37493 1.16138 7.8045 1.16138H6.5892L5.73034 0.302516Z"
        fill={color}
      />
    </g>
    <defs>
      <clipPath id="clip0_bw_all">
        <rect width="10" height="10" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

function DirectionIcon({ direction }) {
  const IconComponent = direction === "sent" ? ArrowUp : Plus;
  return (
    <div className="w-[40px] h-[40px] rounded-full bg-[#EB3366] flex items-center justify-center">
      <IconComponent className="w-[16px] h-[16px] text-white" />
    </div>
  );
}

export default function BusinessSuiteTransactionHistoryAllModal({
  open,
  onOpenChange,
  businessId,
  onSelectRow,
}) {
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [sortBy, setSortBy] = useState("date-desc");

  const { data: txPage, isLoading } = useBusinessWalletTransactions(
    businessId,
    {
      page: 1,
      limit: 100,
      sort: "date_desc",
    },
    { enabled: Boolean(open) && Boolean(businessId) }
  );

  const displayRows = useMemo(() => {
    const raw = Array.isArray(txPage?.results) ? txPage.results : [];
    let filtered = raw;

    if (filterType !== "all") {
      const ft = filterType.toLowerCase();
      filtered = filtered.filter(
        (tx) => String(tx.transactionSubtype || "").toLowerCase() === ft
      );
    }

    if (filterStatus !== "all") {
      const fs = filterStatus.toLowerCase();
      filtered = filtered.filter((tx) => {
        const s = String(tx.status || "").toLowerCase();
        if (fs === "complete") {
          return s === "complete" || s === "completed";
        }
        if (fs === "awaiting settlement") {
          return s.includes("awaiting") || s.includes("settlement");
        }
        return s === fs;
      });
    }

    const mapped = filtered
      .map((tx) => ({ tx, row: mapBusinessApiTxToRow(tx) }))
      .filter((x) => x.row != null);

    const sorted = [...mapped].sort((a, b) => {
      if (sortBy === "date-desc") {
        const da = a.tx.createdAt ? new Date(a.tx.createdAt).getTime() : 0;
        const db = b.tx.createdAt ? new Date(b.tx.createdAt).getTime() : 0;
        return db - da;
      }
      if (sortBy === "date-asc") {
        const da = a.tx.createdAt ? new Date(a.tx.createdAt).getTime() : 0;
        const db = b.tx.createdAt ? new Date(b.tx.createdAt).getTime() : 0;
        return da - db;
      }
      const na = getBusinessWalletTransactionSortAmount(a.tx);
      const nb = getBusinessWalletTransactionSortAmount(b.tx);
      if (sortBy === "amount-desc") {
        return nb - na;
      }
      if (sortBy === "amount-asc") {
        return na - nb;
      }
      return 0;
    });

    return sorted.map((x) => x.row);
  }, [txPage?.results, filterType, filterStatus, sortBy]);

  const apiResultCount = Array.isArray(txPage?.results)
    ? txPage.results.length
    : 0;
  const totalOnServer = txPage?.totalItems;
  const showTruncationNote =
    typeof totalOnServer === "number" &&
    totalOnServer > apiResultCount &&
    apiResultCount > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[1200px] w-[95vw] rounded-2xl p-0 flex flex-col max-h-[90vh] bg-white">
        <DialogClose asChild>
          <Button
            variant="ghost"
            type="button"
            onClick={() => onOpenChange(false)}
            className="w-8 h-8 sm:w-10 sm:h-10 absolute top-3 right-3 sm:top-4 sm:right-4 text-gray-400 hover:text-gray-600 rounded-full p-0 flex items-center justify-center border border-gray-200 hover:bg-gray-100 z-10"
          >
            <X size={16} className="text-gray-700 sm:w-[18px] sm:h-[18px]" />
          </Button>
        </DialogClose>

        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <div className="flex justify-between items-center pr-10">
            <DialogTitle className="text-[#6D6584] text-[20px] font-semibold">
              All transaction history
            </DialogTitle>
            <div className="items-center gap-2 hidden md:flex">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className={`h-[48px] flex items-center justify-center w-full lg:!w-[107px] bg-white border border-[#EBE9EF] text-[#6D6584] rounded-full text-[14px] font-semibold hover:bg-gray-50 transition-colors gap-2 ${
                      filterType !== "all" || filterStatus !== "all"
                        ? "border-[#EB3366] bg-[#EB3366]/5"
                        : ""
                    }`}
                  >
                    <span>Filter</span>
                    <img
                      src={filterIcon}
                      alt=""
                      className="w-[16px] h-[16px] text-[#6D6584]"
                    />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>Filter by Type</DropdownMenuLabel>
                  <DropdownMenuRadioGroup
                    value={filterType}
                    onValueChange={setFilterType}
                  >
                    {TRANSACTION_HISTORY_FILTER_TYPE_OPTIONS.map(
                      ({ value, label }) => (
                        <DropdownMenuRadioItem key={value} value={value}>
                          {label}
                        </DropdownMenuRadioItem>
                      )
                    )}
                  </DropdownMenuRadioGroup>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel>Filter by Status</DropdownMenuLabel>
                  <DropdownMenuRadioGroup
                    value={filterStatus}
                    onValueChange={setFilterStatus}
                  >
                    {TRANSACTION_HISTORY_FILTER_STATUS_OPTIONS.map(
                      ({ value, label }) => (
                        <DropdownMenuRadioItem key={value} value={value}>
                          {label}
                        </DropdownMenuRadioItem>
                      )
                    )}
                  </DropdownMenuRadioGroup>
                  {(filterType !== "all" || filterStatus !== "all") && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => {
                          setFilterType("all");
                          setFilterStatus("all");
                        }}
                        className="text-red-600 focus:text-red-600"
                      >
                        Clear Filters
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className={`h-[48px] flex items-center justify-center w-full lg:!w-[107px] bg-white border border-[#EBE9EF] text-[#6D6584] rounded-full text-[14px] font-semibold hover:bg-gray-50 transition-colors gap-2 ${
                      sortBy !== "date-desc"
                        ? "border-[#EB3366] bg-[#EB3366]/5"
                        : ""
                    }`}
                  >
                    <span>Sort</span>
                    <img
                      src={sortIcon}
                      alt=""
                      className="w-[16px] h-[16px] text-[#6D6584]"
                    />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>Sort by</DropdownMenuLabel>
                  <DropdownMenuRadioGroup
                    value={sortBy}
                    onValueChange={setSortBy}
                  >
                    <DropdownMenuRadioItem value="date-desc">
                      Date (Newest First)
                    </DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="date-asc">
                      Date (Oldest First)
                    </DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="amount-desc">
                      Amount (Highest First)
                    </DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="amount-asc">
                      Amount (Lowest First)
                    </DropdownMenuRadioItem>
                  </DropdownMenuRadioGroup>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {showTruncationNote && (
            <p className="text-sm text-[#6D6584] pb-3">
              Showing {apiResultCount} of {totalOnServer} transactions (up to
              100 per request).
            </p>
          )}
          {isLoading && (
            <p className="flex items-center justify-center py-8 text-[#6D6584] text-sm">
              Loading transactions…
            </p>
          )}
          {!isLoading && (
            <>
              <div className="px-0 pb-4 hidden md:grid grid-cols-12 gap-4">
                <div className="col-span-4 text-[#6D6584] font-semibold text-sm">
                  Action
                </div>
                <div className="col-span-4 text-[#6D6584] font-semibold text-[14px]">
                  Amount
                </div>
                <div className="col-span-3 text-[#6D6584] font-semibold text-[14px]">
                  Status
                </div>
                <div className="col-span-1" />
              </div>

              <div className="md:space-y-7 space-y-3 pt-2">
                {displayRows.length > 0 ? (
                  displayRows.map((row) => {
                    const status = row.status;
                    return (
                      <div
                        key={row.id}
                        className="flex items-center justify-between gap-4 md:grid md:grid-cols-12 md:gap-4 md:border-none border border-[#F1EDF7] border-[1px] rounded-2xl p-4 md:p-0"
                      >
                        <div className="col-span-4 flex items-center gap-3 min-w-0">
                          <div className="relative flex-shrink-0">
                            <DirectionIcon direction={row.direction} />
                          </div>
                          <div className="min-w-0 flex-1 md:space-y-2 space-y-1">
                            <div className="font-semibold text-[#33125F] text-[16px] md:text-[20px]">
                              <span className="md:hidden">
                                {row.actionLabelMobile}
                              </span>
                              <span className="hidden md:inline">
                                {row.actionLabel}
                              </span>
                            </div>
                            <div className="text-[#6D6584] text-xs md:text-[14px]">
                              {row.time}
                            </div>
                          </div>
                        </div>

                        <div className="col-span-4 hidden md:flex flex-col space-y-2">
                          <div className="font-semibold text-[#33125F] text-[16px] md:text-[20px]">
                            {row.primaryAmount}
                          </div>
                          {row.secondaryAmount ? (
                            <div className="text-[#6D6584] text-xs md:text-[14px]">
                              {row.secondaryAmount}
                            </div>
                          ) : null}
                        </div>

                        <div className="md:hidden flex flex-col items-end">
                          <div className="font-semibold text-[#33125F] text-[16px] md:text-[20px]">
                            {row.primaryAmount}
                          </div>
                          {row.secondaryAmount ? (
                            <div className="text-[#6D6584] text-xs md:text-[14px]">
                              {row.secondaryAmount}
                            </div>
                          ) : null}
                          {status ? (
                            <div className="flex items-center gap-2 text-[14px] font-bold mt-1">
                              {status === "Pending" ||
                              status === "Awaiting Settlement" ? (
                                <div className="text-[10px] font-bold text-[#6375E6]">
                                  {status.toUpperCase()}
                                </div>
                              ) : status === "Complete" ||
                                status === "Completed" ? (
                                <div className="text-[10px] font-bold text-[#17C3A5]">
                                  {status.toUpperCase()}
                                </div>
                              ) : (
                                <div className="text-[10px] font-bold text-[#FFC107]">
                                  {status.toUpperCase()}
                                </div>
                              )}
                            </div>
                          ) : null}
                        </div>

                        <div className="col-span-3 hidden md:block">
                          {status ? (
                            <div className="flex items-center gap-2 text-[14px] font-bold">
                              {status === "Pending" ||
                              status === "Awaiting Settlement" ? (
                                <div className="flex items-center gap-2 bg-[#EFF1FC] px-3 py-1 rounded-full">
                                  <StatusIcon color="#6375E6" />
                                  <span className="text-[10px] font-bold text-[#6375E6]">
                                    {status.toUpperCase()}
                                  </span>
                                </div>
                              ) : status === "Complete" ||
                                status === "Completed" ? (
                                <div className="flex items-center gap-2 bg-[#E8F9F6] px-3 py-1 rounded-full">
                                  <StatusIcon color="#17C3A5" />
                                  <span className="text-[10px] font-bold text-[#17C3A5]">
                                    {status.toUpperCase()}
                                  </span>
                                </div>
                              ) : (
                                <div className="flex items-center gap-2 bg-[#FFEFD5] px-3 py-1 rounded-full">
                                  <StatusIcon color="#FFC107" />
                                  <span className="text-[10px] font-bold text-[#FFC107]">
                                    {status.toUpperCase()}
                                  </span>
                                </div>
                              )}
                            </div>
                          ) : null}
                        </div>

                        <div className="col-span-1 justify-end hidden md:flex">
                          <Button
                            type="button"
                            onClick={() => {
                              onOpenChange(false);
                              onSelectRow?.(row);
                            }}
                            variant="outline"
                            size="icon"
                            className="rounded-full flex-shrink-0 border-gray-200 hover:bg-gray-200 h-10 w-10"
                            aria-label="View details"
                          >
                            <ArrowRight className="w-4 h-4 text-[#6D6584]" />
                          </Button>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p className="flex items-center justify-center py-8 text-[#6D6584]">
                    No transactions found.
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
